import type { ElementType, ReactNode } from "react";
import { Box, ListItem, ListItemButton, Stack, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";

/**
 * One dense list row: leading visual, two lines of text, one trailing element.
 * Render inside a MUI <List>. Without `to` the row is plain, not a link.
 */
export function DenseRow({
  to,
  ariaLabel,
  leading,
  title,
  subtitle,
  trailing,
  titleComponent = "p",
}: {
  to?: string;
  /** Accessible name for the row link when the source supplies one. */
  ariaLabel?: string;
  leading: ReactNode;
  title: ReactNode;
  subtitle: ReactNode;
  trailing?: ReactNode;
  /** Element for the title, e.g. "h3" when rows sit under a section heading. */
  titleComponent?: ElementType;
}) {
  const rowSx = { gap: 1.5, px: { xs: 0.5, sm: 1 }, py: 0.75, borderRadius: 2, alignItems: "center" } as const;
  const body = (
    <>
        {leading}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            component={titleComponent}
            sx={{
              m: 0,
              fontSize: "0.9375rem",
              fontWeight: 600,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              overflowWrap: "anywhere",
            }}
          >
            {title}
          </Typography>
          <Typography noWrap variant="body2" color="text.secondary">
            {subtitle}
          </Typography>
        </Box>
        {trailing}
    </>
  );
  return (
    <ListItem disablePadding>
      {to ? (
        <ListItemButton component={RouterLink} to={to} aria-label={ariaLabel} sx={rowSx}>
          {body}
        </ListItemButton>
      ) : (
        <Stack direction="row" sx={{ ...rowSx, width: "100%" }}>
          {body}
        </Stack>
      )}
    </ListItem>
  );
}
